import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp, FaGithub, FaLinkedinIn } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Contact = () => {

  // Animation variants for staggered children
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <ContactWrapper id="contact">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Have a project in mind or want to discuss potential opportunities? 
            I'd love to hear from you. Let's connect and create something amazing together!
          </p>
        </motion.div>
        
        <div className="contact-container">
          <motion.div 
            className="contact-illustration"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src="https://img.icons8.com/clouds/500/000000/contact-card.png" 
              alt="Contact illustration" 
              style={{ width: '100%', height: 'auto' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://img.icons8.com/clouds/500/000000/contact-card.png';
              }}
            />
          </motion.div>
          
          <motion.div 
            className="contact-info"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <motion.div className="info-item" variants={item}>
              <div className="info-icon">
                <FaEnvelope className="icon-animate" />
              </div>
              <div className="info-content">
                <h3>Email</h3>
                <a href="mailto:agarwalakash491@gmail.com" className="link-hover">agarwalakash491@gmail.com</a>
              </div>
            </motion.div>
            
            <motion.div className="info-item" variants={item}>
              <div className="info-icon">
                <FaWhatsapp className="icon-animate" />
              </div>
              <div className="info-content">
                <h3>WhatsApp</h3>
                <a href="https://wa.me/918999194346" target="_blank" rel="noopener noreferrer" className="link-hover">Message me on WhatsApp</a>
              </div>
            </motion.div>
            
            <motion.div className="info-item" variants={item}>
              <div className="info-icon">
                <FaMapMarkerAlt className="icon-animate" />
              </div>
              <div className="info-content">
                <h3>Location</h3>
                <span>Pune, India</span>
              </div>
            </motion.div>
            <motion.div className="social-links" variants={item}>
              <a 
                href="https://github.com/Akash491" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="GitHub"
                className="social-link"
              >
                <FaGithub />
              </a>
              <a 
                href="https://www.linkedin.com/in/agarwalakash491/" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="LinkedIn"
                className="social-link"
              >
                <FaLinkedinIn />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </ContactWrapper>
  );
};

const ContactWrapper = styled.section`
  background-color: #f8fafc;
  padding: 5rem 0;
  position: relative;
  overflow: hidden;

  .section-subtitle {
    text-align: center;
    max-width: 700px;
    margin: 0 auto 3rem;
    color: ${({ theme }) => theme.colors.gray};
    font-size: 1.1rem;
    line-height: 1.8;
  }

  .contact-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    background: white;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }

  .contact-info {
    padding: 3rem;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  
  .info-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 25px;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateX(5px);
      
      .icon-animate {
        transform: scale(1.1);
        color: ${({ theme }) => theme.primary};
      }
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .info-icon {
    background: ${({ theme }) => theme.bgLight};
    color: ${({ theme }) => theme.primary};
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    flex-shrink: 0;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    border: 1px solid ${({ theme }) => theme.border};
    
    @media (max-width: 480px) {
      width: 42px;
      height: 42px;
      min-width: 42px;
      font-size: 1.1rem;
      margin-right: 12px;
    }
    
    .icon-animate {
      transition: all 0.3s ease;
    }
  }
  
  .info-content {
    h3 {
      margin: 0 0 8px;
      color: ${({ theme }) => theme.textPrimary};
      font-size: 1.2rem;
      font-weight: 600;
    }
    
    a, span {
      color: ${({ theme }) => theme.textSecondary};
      text-decoration: none;
      transition: all 0.3s ease;
      display: inline-block;
      font-size: 0.95rem;
      word-break: break-all;
      overflow-wrap: break-word;
      max-width: 100%;
      
      &.link-hover {
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -2px;
          left: 0;
          background: ${({ theme }) => theme.primary};
          transition: width 0.3s ease;
        }
        
        &:hover {
          color: ${({ theme }) => theme.primary};
          
          &::after {
            width: 100%;
          }
        }
      }
    }
  }
  
  .social-links {
    display: flex;
    gap: 1.5rem;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .social-link {
    color: white;
    font-size: 1.5rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    
    &:hover {
      transform: translateY(-3px) scale(1.1);
      background: rgba(255, 255, 255, 0.2);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: translateY(1px) scale(0.98);
      border: 1px solid ${({ theme }) => theme.border};
      
      &:hover {
        background: ${({ theme }) => theme.primary};
        color: white;
        transform: translateY(-3px) scale(1.05);
        box-shadow: 0 5px 15px ${({ theme }) => theme.primary}30;
      }
    }
  }
  
  @media (max-width: 992px) {
    .contact-info {
      padding: 2rem 1.5rem;
    }
    
    .info-content {
      h3 {
        font-size: 1.1rem;
        margin-bottom: 6px;
      }
      
      a, span {
        font-size: 0.9rem;
      }
    }
  }
  }

  .form-group {
    position: relative;
    margin-bottom: 1.8rem;

    input, textarea {
      width: 100%;
      padding: 1rem 1rem 1rem 0;
      font-size: 1rem;
      color: ${({ theme }) => theme.colors.dark};
      border: none;
      border-bottom: 2px solid #e5e7eb;
      outline: none;
      background: transparent;
      transition: all 0.3s ease;

      &:focus, &:valid {
        border-color: ${({ theme }) => theme.colors.primary};
      }

      &:focus + label, &:valid + label {
        transform: translateY(-1.5rem) scale(0.9);
        color: ${({ theme }) => theme.colors.primary};
      }
    }

    textarea {
      resize: none;
      min-height: 100px;
    }

    label {
      position: absolute;
      top: 1rem;
      left: 0;
      color: ${({ theme }) => theme.colors.gray};
      pointer-events: none;
      transition: all 0.3s ease;
    }
  }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;
    padding: 0.8rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 1rem;

    &:hover {
      background: ${({ theme }) => theme.colors.secondary};
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }

    .icon {
      margin-left: 0.5rem;
      font-size: 1rem;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .contact-container {
      grid-template-columns: 1fr;
    }

    .contact-info {
      border-radius: 15px 15px 0 0;
    }
  }
`;

export default Contact;
